<template>
    <DCard
        :title="__('settings.orders.order_clients.OneWebshop')"
        padding
    >
        <DFormRow
            :label="__('settings.order_client.form.order_client')"
            :hint="__('settings.order_client.form.order_client_hint_one_webshop')"
        >
            <DSwitch
                v-model="form.enabled"
                color="success"
                variant="3d"
                @update:modelValue="$emit('one-webshop-valid', !form.enabled || credentialsAreValid)"
            />
        </DFormRow>
        <DRow v-if="form.enabled">
            <DFormRow
                :label="__('settings.order_client.form.api_url')"
                :is-required="true"
                :error="errorsData.api_url ?? errorsData['OneWebshop.api_url']"
                :hint="__('settings.order_client.form.api_url_hint_one_webshop')"
            >
                <DInput
                    id="api_url"
                    v-model="form.api_url"
                    :placeholder="__('settings.order_client.form.api_url')"
                    :is-valid="isFormFieldValid('api_url')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.auth_type')"
                :hint="__('settings.order_client.form.auth_type_hint')"
            >
                <DSelect
                    id="auth-type-dropdown"
                    v-model="authType"
                    :options="authTypeOptions"
                />
            </DFormRow>
            <template v-if="authType === 'basic_auth'">
                <DFormRow
                    :label="__('settings.order_client.form.username')"
                    :is-required="true"
                    :error="errorsData.username ?? errorsData['OneWebshop.username']"
                    :hint="__('settings.order_client.form.username_hint_one_webshop')"
                >
                    <DInput
                        id="consumer_key"
                        v-model="form.username"
                        :placeholder="__('settings.order_client.form.username')"
                        :is-valid="isFormFieldValid('username')"
                        type="text"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.order_client.form.password')"
                    :is-required="true"
                    :error="errorsData.password ?? errorsData['OneWebshop.password']"
                    :hint="__('settings.order_client.form.password_hint_one_webshop')"
                >
                    <DInput
                        id="consumer_secret"
                        v-model="form.password"
                        :placeholder="__('settings.order_client.form.password')"
                        :is-valid="isFormFieldValid('password')"
                        type="text"
                    />
                </DFormRow>
            </template>
            <DFormRow
                v-else
                :label="__('settings.order_client.form.api_key')"
                :is-required="true"
                :error="errorsData.api_key ?? errorsData['OneWebshop.api_key']"
                :hint="__('settings.order_client.form.api_key_hint_one_webshop')"
            >
                <DInput
                    id="api_key"
                    v-model="form.api_key"
                    :placeholder="__('settings.order_client.form.api_key')"
                    :is-valid="isFormFieldValid('api_key')"
                    type="text"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.process_new_customers')"
                :error="errorsData.process_new_customers ?? errorsData['OneWebshop.process_new_customers']"
                :hint="__('settings.order_client.form.process_new_customers_hint_one_webshop')"
            >
                <DSwitch
                    id="process_new_customers"
                    v-model="form.process_new_customers"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.exclude_unknown_customers_orders')"
                :error="errorsData.exclude_unknown_customers_orders ?? errorsData['OneWebshop.exclude_unknown_customers_orders']"
                :hint="__('settings.order_client.form.exclude_unknown_customers_orders_hint_one_webshop')"
            >
                <DSwitch
                    id="exclude_unknown_customers_orders"
                    v-model="form.exclude_unknown_customers_orders"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.enable_order_confirmation_email')"
                :error="errorsData.enable_order_confirmation_email ?? errorsData['OneWebshop.enable_order_confirmation_email']"
                :hint="__('settings.order_client.form.enable_order_confirmation_email_hint_one_webshop')"
            >
                <DSwitch
                    id="enable_order_confirmation_email"
                    v-model="form.enable_order_confirmation_email"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.allow_manual_approval_for_existing_customers')"
                :error="errorsData.allow_manual_approval_for_existing_customers ?? errorsData['OneWebshop.allow_manual_approval_for_existing_customers']"
                :hint="__('settings.order_client.form.allow_manual_approval_for_existing_customers_hint')"
            >
                <DSwitch
                    id="allow_manual_approval_for_existing_customers"
                    v-model="form.allow_manual_approval_for_existing_customers"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.always_use_banktransfer_as_recurring_payment_method')"
                :error="errorsData.always_use_banktransfer_as_recurring_payment_method ?? errorsData['OneWebshop.always_use_banktransfer_as_recurring_payment_method']"
                :hint="__('settings.order_client.form.always_use_banktransfer_as_recurring_payment_method_hint')"
            >
                <DSwitch
                    id="always_use_banktransfer_as_recurring_payment_method"
                    v-model="form.always_use_banktransfer_as_recurring_payment_method"
                    color="success"
                    variant="3d"
                />
            </DFormRow>
            <DFormRow
                :label="__('settings.order_client.form.prepaid_invoice_behavior')"
                :error="errorsData.prepaid_invoice_behavior ?? errorsData['OneWebshop.prepaid_invoice_behavior']"
                :hint="__('settings.order_client.form.prepaid_invoice_behavior_hint_one_webshop')"
            >
                <DSelect
                    id="prepaid_invoice_behavior"
                    v-model="form.prepaid_invoice_behavior"
                    :options="prepaidInvoiceBehaviorTypes"
                    @update:value="updatePrepaidInvoiceBehaviorValue"
                />
            </DFormRow>
            <DRow>
                <DCol sm="4" />
                <DCol sm="4">
                    <div
                        v-if="connectionStatus === 'successful'"
                        class="green-text"
                    >
                        {{ __('settings.connections.connection_successful') }}
                    </div>
                    <div
                        v-if="connectionStatus === 'unsuccessful'"
                        class="red-text"
                    >
                        {{ __('settings.connections.connection_unsuccessful') }}
                    </div>
                </DCol>
                <DCol
                    sm="4"
                    class="check-database-connection-btn"
                >
                    <DButton
                        icon="lan-connect"
                        :label="__('settings.connections.check_connection')"
                        :disabled="isInLoadingState"
                        @click="checkConnection"
                    />
                </DCol>
                <DConnectionErrorModal
                    :show-modal="connectionException !== null"
                    :exception="connectionException"
                    @close-modal="closeErrorCheckModal"
                />
            </DRow>
        </DRow>

        <DRow v-if="form.enabled">
            <hr>
            <p>{{ __('settings.order_client.form.fraud_recommendation_settings') }}</p>
            <hr>
            <DCol>
                <div class="bg-secondary text-white">
                    <h6 class="text-center p-3 mb-2">
                        Existing Customers Rules
                    </h6>
                </div>
                <DTable
                    :items="form.order_recommendation_logic['existing_customers']"
                    :fields="fields"
                >
                    <template #fraud_score="{item}">
                        <td>
                            <h5>
                                <DBadge :color="getBadgeColor(item.fraud_score)">
                                    {{ item.fraud_score }}
                                </DBadge>
                            </h5>
                        </td>
                    </template>
                    <template #action="{item}">
                        <td>
                            <div style="display: flex; grid-gap: 10px;">
                                <DSelect
                                    id="recommendation_existing_customers"
                                    v-model="item.recommendation"
                                    :options="recommendations"
                                    :placeholder="__('orders.recommendation')"
                                    :is-valid="isFormFieldValid('recommendation_existing_customers')"
                                    @update:value="(value) => { mapRecommendation(value, item, 'existing_customers') }"
                                />
                            </div>
                        </td>
                    </template>
                </DTable>
            </DCol>
            <DCol>
                <div class="bg-secondary text-white">
                    <h6 class="text-center p-3 mb-2">
                        New Customers Rules
                    </h6>
                </div>
                <DTable
                    :items="form.order_recommendation_logic['new_customers']"
                    :fields="fields"
                >
                    <template #fraud_score="{item}">
                        <td>
                            <h5>
                                <DBadge :color="getBadgeColor(item.fraud_score)">
                                    {{ item.fraud_score }}
                                </DBadge>
                            </h5>
                        </td>
                    </template>
                    <template #action="{item}">
                        <td>
                            <div style="display: flex; grid-gap: 10px;">
                                <DSelect
                                    id="recommendation_new_customers"
                                    v-model="item.recommendation"
                                    :options="recommendations"
                                    :placeholder="__('orders.recommendation')"
                                    :is-valid="isFormFieldValid('recommendation_new_customers')"
                                    @update:value="(value) => { mapRecommendation(value, item, 'new_customers') }"
                                />
                            </div>
                        </td>
                    </template>
                </DTable>
            </DCol>
        </DRow>
    </DCard>
</template>

<script setup>

import { route } from '~@/Composables/route'
import { ref, onUpdated } from 'vue'
import { useForm } from '@inertiajs/vue3'
import DConnectionErrorModal from '~@/Components/Common/DConnectionErrorModal'

const props = defineProps({
    oneWebshopSettings: {
        type: Object,
        default: () => { return {} }
    },
    errors: {
        type: Object,
        default: () => { return {} }
    },
    orderRecommendationLogic: {
        type: Object,
        default: () => { return {} }
    },
    recommendations: {
        type: Array,
        default: () => { return [] }
    },
    prepaidInvoiceBehaviorTypes: {
        type: Array,
        default: () => { return [] }
    }
})

const fields = [
    { key: 'fraud_score', label: 'Fraud Score' },
    { key: 'action', label: 'Recommendation' }
]

const authTypeOptions = [
    { label: 'Basic Auth', value: 'basic_auth' },
    { label: 'API Key', value: 'api_key' },
]

const getBadgeColor = function (score) {
    switch (score) {
        case 'NONE':
            return 'secondary'
        case 'LOW':
            return 'success'
        case 'MEDIUM':
            return 'warning'
        case 'HIGH':
            return 'danger'
        default:
            return 'secondary'
    }
}

const emit = defineEmits(['one-webshop-valid', 'one-webshop-data'])

const form = useForm({
    api_url: props.oneWebshopSettings.api_url ?? null,
    username: props.oneWebshopSettings.username,
    password: props.oneWebshopSettings.password,
    api_key: props.oneWebshopSettings.api_key,
    order_client: props.oneWebshopSettings.order_client ?? 'OneWebshop',
    enabled: props.oneWebshopSettings.enabled ?? false,
    exclude_unknown_customers_orders: props.oneWebshopSettings.exclude_unknown_customers_orders ?? false,
    process_new_customers: props.oneWebshopSettings.process_new_customers ?? false,
    enable_order_confirmation_email: props.oneWebshopSettings.enable_order_confirmation_email ?? false,
    order_recommendation_logic: props.orderRecommendationLogic,
    prepaid_invoice_behavior: props.oneWebshopSettings.prepaid_invoice_behavior,
    allow_manual_approval_for_existing_customers: props.oneWebshopSettings.allow_manual_approval_for_existing_customers,
    always_use_banktransfer_as_recurring_payment_method: props.oneWebshopSettings.always_use_banktransfer_as_recurring_payment_method,
})

const connectionStatus = ref('')
const apiError = ref(null)
const errorsData = ref(props.errors)
const credentialsAreValid = ref(false)
const connectionException = ref(null)
const authType = ref('basic_auth')

const mapRecommendation = function (value, item, key) {
    const index = form.order_recommendation_logic[key].findIndex(obj => obj.id === item.id)

    form.order_recommendation_logic[key][index].recommendation = value
}

emit('one-webshop-valid', !form.enabled || credentialsAreValid.value)
emit('one-webshop-data', form)

const checkConnection = () => {
    errorsData.value = {}
    connectionException.value = null
    const params = {
        order_client: form.order_client,
        api_url: form.api_url
    }
    if (authType.value === 'basic_auth') {
        params.username = form.username
        params.password = form.password
    } else {
        params.api_key = form.api_key
    }
    window.axios.post(route('settings.order-settings.check-one-webshop', params))
        .then(function () {
            connectionStatus.value = 'successful'
            credentialsAreValid.value = true
            emit('one-webshop-valid', !form.enabled || credentialsAreValid.value)
        })
        .catch(function (error) {
            connectionStatus.value = 'unsuccessful'
            credentialsAreValid.value = false
            if (error.response.data.api_error !== undefined) {
                connectionException.value = error.response.data.api_error
            }

            if (error.response.data.errors !== undefined) {
                errorsData.value = error.response.data.errors
                for (const key in errorsData.value) {
                    if (typeof errorsData.value[key] === 'object') {
                        errorsData.value[key] = errorsData.value[key][0]
                    }
                }
            }
            if (error?.response.data.exception !== undefined) {
                connectionException.value = error.response.data.exception
            }
        })
}

const closeErrorCheckModal = () => {
    apiError.value = null
    connectionException.value = null
}

onUpdated(() => {
    errorsData.value = props.errors
})

const updatePrepaidInvoiceBehaviorValue = (value) => {
    form.prepaid_invoice_behavior = value
}
</script>

<script>
export default {
    name: 'OneWebshopCreate'
}
</script>

<style lang="scss" scoped>
.check-database-connection-btn {
    text-align: right;
    padding-bottom: 12px;
}
.green-text {
    color: green;
}
.red-text {
    color: red;
}
</style>
